import { OrderedListOutlined } from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Flex, Image, TableColumnsType, Tag,
} from 'antd';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
    managerQueries, scriptsMutations,
    scriptsQueries,
} from '@/api';
import {
    ActionDeleteButton,
    ActionEditButton,
    ActionsButtonWrapper,
    ActionScriptToggleButton,
    ActionViewButton,
    ConfirmModal,
    CreateButton,
    DefaultTable,
    DeleteProjectModal,
    ReorderModal,
    RoleGuard,
} from '@/components';
import {
    AppRoute, DEFAULT_ORDER_DIRECTION, UserRole,
} from '@/constants';
import {
    useCustomParams, usePopupQueryParams,
} from '@/hooks';
import { useUserStore } from '@/stores';
import {
    DefaultOrderColumn, Script, SortDirection,
} from '@/types';
import {
    getActionsColumn,
    getScriptExpandableConfig,
    getScriptImageColumn,
    getScriptPriceColumn,
    getScriptProductIdColumn,
    getScriptTitleColumn,
    getUpdatedAtColumn,
} from '@/utils';

type ProjectScriptsTabProps = {
    projectId?: string;
    type: 'default' | 'cross-sale';
};

export const ProjectScriptsTab = ({ projectId, type }: ProjectScriptsTabProps) => {
    const navigate = useNavigate();
    const { currentPage, pageSize, search } = useCustomParams();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [scriptToDelete, setScriptToDelete] = useState<Script | null>(null);
    const [toggleModalOpen, setToggleModalOpen] = useState(false);
    const [scriptToToggle, setScriptToToggle] = useState<Script | null>(null);
    const [reorderModalOpen, setReorderModalOpen] = useState(false);
    const popupQuery = usePopupQueryParams();
    const { user } = useUserStore();
    const isManager = useMemo(() => user?.role === UserRole.Manager, [user?.role]);

    const deleteMutationOptions = useMemo(
        () => scriptToDelete && projectId
            ? scriptsMutations.delete(projectId, scriptToDelete.id)
            : scriptsMutations.delete('', ''),
        [scriptToDelete, projectId],
    );

    const finalOrderColumn = useMemo(() => 'priority', []);
    const finalOrderDirection = useMemo(() => DEFAULT_ORDER_DIRECTION, []);

    const { data: scriptsData, isLoading: isLoadingScripts } = useQuery({
        ...(isManager
            ? managerQueries.getManagerScripts(projectId || '', {
                type,
                per_page: pageSize,
                current_page: currentPage,
                order_column: finalOrderColumn as DefaultOrderColumn,
                order_direction: finalOrderDirection as SortDirection,
                search,
            })
            : scriptsQueries.getAllScripts(projectId || '', {
                type,
                per_page: pageSize,
                current_page: currentPage,
                order_column: finalOrderColumn as DefaultOrderColumn,
                order_direction: finalOrderDirection as SortDirection,
                search,
            })),
        enabled: !!projectId,
    });

    const total = useMemo(() => {
        if (!search || !scriptsData) {
            return scriptsData?.total || 0;
        }

        return scriptsData.total;
    }, [scriptsData, search]);


    const {
        data: reorderScriptsData,
        isFetching: isFetchingReorderScripts,
    } = useQuery({
        ...scriptsQueries.getAllScripts(projectId || '', {
            type,
            per_page: -1,
            current_page: 1,
            order_column: 'priority',
            order_direction: 'asc',
        }),
        enabled: reorderModalOpen && !!projectId && !isManager,
    });

    const filteredScripts = useMemo(() => {
        if (!search || !scriptsData) {
            return scriptsData?.items || [];
        }

        const searchLower = search.toLowerCase();
        return scriptsData.items.filter(
            (script) =>
                script.title?.toLowerCase().includes(searchLower) ||
                script.product_sku?.toLowerCase().includes(searchLower) ||
                script.product_id?.toLowerCase().includes(searchLower) ||
                (script.product_price != null && String(script.product_price).toLowerCase().includes(searchLower)),
        );
    }, [scriptsData, search]);

    const previewScriptId = useMemo(() => {
        if (popupQuery.mode !== 'image' || !popupQuery.itemId) {
            return null;
        }

        return popupQuery.itemId;
    }, [popupQuery.itemId, popupQuery.mode]);

    const previewScript = useMemo(
        () => filteredScripts.find((script) => script.id === previewScriptId) || null,
        [filteredScripts, previewScriptId],
    );

    const { data: fetchedPreviewScript } = useQuery({
        ...(isManager
            ? managerQueries.getManagerScript(previewScriptId || '')
            : scriptsQueries.getOneScript(previewScriptId || '', projectId)),
        enabled: !!previewScriptId && !!projectId && !previewScript,
    });

    const resolvedPreviewScript = previewScript || fetchedPreviewScript || null;

    useEffect(() => {
        if (!import.meta.env.DEV || filteredScripts.length === 0) {
            return;
        }
        const seen = new Set<string>();
        for (const script of filteredScripts) {
            const id = script.id;
            if (id == null || id === '') {
                console.warn('[ProjectScriptsTab] Script row missing id', script);
                continue;
            }
            if (seen.has(id)) {
                console.warn('[ProjectScriptsTab] Duplicate script id in table data:', id);
            }
            seen.add(id);
        }
    }, [filteredScripts]);

    const baseScriptExpandable = useMemo(() => getScriptExpandableConfig(), []);

    const { mutate: deleteScript, isPending: isDeleting } = useMutation(deleteMutationOptions);
    const { mutate: reorderScripts, isPending: isReordering } = useMutation(
        scriptsMutations.reorder(projectId || ''),
    );

    const toggleMutationOptions = useMemo(
        () => scriptToToggle && projectId
            ? scriptsMutations.toggleActive(projectId, scriptToToggle.id)
            : scriptsMutations.toggleActive('', ''),
        [scriptToToggle, projectId],
    );

    const { mutate: toggleScriptActive, isPending: isToggling } = useMutation(toggleMutationOptions);

    const handleDelete = useCallback((record: Script) => {
        setScriptToDelete(record);
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (scriptToDelete) {
            deleteScript(undefined, {
                onSuccess: () => {
                    setDeleteModalOpen(false);
                    setScriptToDelete(null);
                },
            });
        }
    }, [scriptToDelete, deleteScript]);

    const handleDeleteCancel = useCallback(() => {
        setDeleteModalOpen(false);
        setScriptToDelete(null);
    }, []);

    const handleToggleConfirm = useCallback(() => {
        if (scriptToToggle) {
            toggleScriptActive(undefined, {
                onSuccess: () => {
                    setToggleModalOpen(false);
                    setScriptToToggle(null);
                },
            });
        }
    }, [scriptToToggle, toggleScriptActive]);

    const handleToggleCancel = useCallback(() => {
        setToggleModalOpen(false);
        setScriptToToggle(null);
    }, []);

    const handlePreviewOpen = useCallback((record: Script) => {
        void popupQuery.open({
            mode: 'image',
            itemId: record.id,
            imageIndex: 0,
        });
    }, [popupQuery]);

    const handlePreviewVisibleChange = useCallback((visible: boolean) => {
        if (visible) {
            return;
        }

        void popupQuery.close();
    }, [popupQuery]);

    const recordIsActive = Boolean(scriptToToggle?.is_active ?? true);

    const columns = useMemo<TableColumnsType<Script>>(() => [
        getScriptImageColumn({
            onPreviewOpen: handlePreviewOpen,
        }),
        {
            ...getScriptTitleColumn({
                onClick: (record) => {
                    if (projectId) {
                        navigate(`/projects/${projectId}/scripts/${record.id}`);
                    } else {
                        navigate(`${AppRoute.Scripts}/${record.id}`);
                    }
                },
            }),
            sorter: false,
            sortDirections: undefined,
        },
        {
            ...getUpdatedAtColumn<Script>(),
            sorter: false,
            sortDirections: undefined,
        },
        // getScriptPriceColumn(),
        getScriptProductIdColumn(),
        getActionsColumn<Script>({
            render: (_: unknown, record: Script) => {
                const viewTo = projectId ? `/projects/${projectId}/scripts/${record.id}` : `${AppRoute.Scripts}/${record.id}`;
                const editTo = projectId ? `/projects/${projectId}/scripts/${record.id}/edit` : `${AppRoute.Scripts}/${record.id}/edit`;

                return (
                    <ActionsButtonWrapper>
                        <ActionViewButton
                            onClick={() => navigate(viewTo)}
                            text={isManager ? "Переглянути" : null}
                            to={viewTo}
                        />
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <ActionsButtonWrapper>
                                <ActionScriptToggleButton
                                    isActive={Boolean(record.is_active ?? true)}
                                    onClick={() => {
                                        setScriptToToggle(record);
                                        setToggleModalOpen(true);
                                    }}
                                />
                                <ActionEditButton
                                    onClick={() => navigate(editTo)}
                                    to={editTo}
                                    tooltipTitle="Редагувати"
                                />
                                <ActionDeleteButton
                                    onClick={() => handleDelete(record)}
                                />
                            </ActionsButtonWrapper>
                        </RoleGuard>
                    </ActionsButtonWrapper>
                );
            },
        }),
    ], [handleDelete, handlePreviewOpen, navigate, projectId]);

    const reorderItems = useMemo(() => {
        return (reorderScriptsData?.items || []).map((script) => ({
            id: script.id,
            title: script.title,
            subtitle: script.product_sku || script.product_id || null,
            extra: (
                <Flex gap="small" wrap justify="flex-end">
                    {script.product_price && <Tag color="green">{script.product_price}</Tag>}
                    <Tag color={script.is_active === false ? 'default' : 'blue'}>
                        {script.is_active === false ? 'Неактивний' : 'Активний'}
                    </Tag>
                </Flex>
            ),
        }));
    }, [reorderScriptsData?.items]);

    const handleReorderSave = useCallback((ids: string[]) => {
        reorderScripts(
            { ids },
            {
                onSuccess: () => {
                    setReorderModalOpen(false);
                },
            },
        );
    }, [reorderScripts]);

    return (
        <>
            <DefaultTable
                title={null}
                columns={columns}
                dataSource={filteredScripts}
                total={total}
                loading={isLoadingScripts}
                rowKey={(record) => record.id}
                hasSearch={true}
                defaultPageSize={50}
                // expandable={baseScriptExpandable}
                renderCreateButton={() => (
                    <Flex gap="small" wrap>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <Button
                                icon={<OrderedListOutlined />}
                                onClick={() => setReorderModalOpen(true)}
                                disabled={isLoadingScripts}
                            >
                                Сортування
                            </Button>
                        </RoleGuard>
                        <RoleGuard roles={[UserRole.SuperAdmin, UserRole.Admin, UserRole.ScriptWriter]}>
                            <CreateButton
                                label="Створити скрипт"
                                disabled={isLoadingScripts}
                                to={projectId ? `/projects/${projectId}/scripts/create?type=${type}` : `${AppRoute.Scripts}/create?type=${type}`}
                            />
                        </RoleGuard>
                    </Flex>
                )}
            />

            {resolvedPreviewScript?.main_image?.url && (
                <Image
                    style={{ display: 'none' }}
                    src={resolvedPreviewScript.main_image.url}
                    alt={resolvedPreviewScript.title}
                    preview={{
                        visible: popupQuery.isMode('image') && popupQuery.itemId === resolvedPreviewScript.id,
                        src: resolvedPreviewScript.main_image.url,
                        onVisibleChange: handlePreviewVisibleChange,
                    }}
                />
            )}

            <DeleteProjectModal
                open={deleteModalOpen}
                entityName={scriptToDelete?.title}
                entityType="script"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                loading={isDeleting}
            />

            <ConfirmModal
                open={toggleModalOpen}
                mode="update"
                entityType="script"
                entityName={scriptToToggle?.title}
                onConfirm={handleToggleConfirm}
                onCancel={handleToggleCancel}
                loading={isToggling}
                confirmTitle={recordIsActive ? 'Деактивувати скрипт?' : 'Активувати скрипт?'}
                confirmMessage={
                    recordIsActive
                        ? 'Скрипт стане неактивним для менеджерів. Продовжити?'
                        : 'Скрипт знову стане активним для менеджерів. Продовжити?'
                }
                confirmOkText={recordIsActive ? 'Деактивувати' : 'Активувати'}
            />

            <ReorderModal
                open={reorderModalOpen}
                title={type === 'cross-sale' ? 'Сортування крос-сейлів' : 'Сортування скриптів'}
                items={reorderItems}
                loading={isFetchingReorderScripts}
                saving={isReordering}
                onCancel={() => setReorderModalOpen(false)}
                onSave={handleReorderSave}
                emptyText={type === 'cross-sale' ? 'Немає крос-сейлів для сортування' : 'Немає скриптів для сортування'}
            />

        </>
    );
};
